import get from 'lodash/get';
import { SESSION_EXPIRED_FAULT_CODE } from '../../api/api.fault.constants';
import { ErrorConverter } from '../../errorHandling/ErrorConverter';
import type { CapturedError } from '../../errorHandling/errorHandling.types';
import type {
  SessionMessage,
  SessionTerminationReason,
  SessionTerminationRecord
} from './sessionTermination.types';
import * as LocalStorage from '../../utility/local.storage';

// ─── Constants ──────────────────────────────────────

/**
 * BroadcastChannel name and localStorage prefix for close routing (and the
 * temporary Angular activity stamp). Not `invision-sessionId` / `invision-systemId`
 * — those stay in sessionStorage as the tab's identity.
 *
 * Angular listens on the same name with the same JSON; the name and message
 * shapes are a cross-stack contract.
 */
export const SESSION_CHANNEL_NAME = 'invision-cross-tab-session-coordinator';

/** Unprefixed so the Angular side can read the same records. */
export const SESSION_RECORD_PREFIX = `${SESSION_CHANNEL_NAME}:termination:`;

/** Point at which the oldest finished record is evicted to make room. */
export const SESSION_RECORD_LIMIT = 128;

/**
 * How long a `closing` claim remains. CloseSession is one request; anything
 * older than the 15-minute idle window was abandoned and must not block a
 * later logout.
 */
export const CLOSING_SESSION_RECORD_TTL_MS = 15 * 60 * 1000;

/**
 * How long a finished or uncertain close remains. Long enough for a background
 * tab to notice it, and short enough that ended sessions do not accumulate in
 * localStorage. Idle timeout is 15 minutes, so an hour covers a missed notice.
 */
export const FINISHED_SESSION_RECORD_TTL_MS = 60 * 60 * 1000;

/**
 * TODO: Remove after Angular apps are retired.
 *
 * Temporary bridge for Angular idle clocks that still share a backend session
 * with a React tab.
 *
 * React idle-timer keeps its own BroadcastChannel (`name` = session id). Angular
 * never joins that channel. Before Angular's 15-minute poll closes the session,
 * it reads `{ updatedAt }` under this prefix. If the stamp is still inside the
 * inactivity window, it refreshes its own clock instead of calling CloseSession
 * on a session React is still using.
 *
 * This is not session-close traffic. Do not publish activity on
 * `SESSION_CHANNEL_NAME` — that channel's storage echo is a single key and would
 * overwrite `SESSION_CLOSED` / `CLOSE_OTHER_SESSIONS`. A live ACTIVITY message
 * also cannot replace the stamp: Angular decides at expiry time, and a missed
 * BroadcastChannel pulse leaves no proof that React was still active.
 *
 * Remove this prefix, `sessionActivityKey`, the React `recordActivity` writer,
 * and the Angular freshness check once Angular apps no longer own an idle clock.
 */
export const SESSION_ACTIVITY_PREFIX = `${SESSION_CHANNEL_NAME}:activity:`;

/**
 * TODO: Remove after Angular apps are retired.
 *
 * How long an activity stamp can still tell Angular that React is using the
 * session. Matches Angular's 15-minute idle window: once `updatedAt` is older
 * than that, the stamp can no longer prevent CloseSession.
 */
export const SESSION_ACTIVITY_TTL_MS = 15 * 60 * 1000;

// ─── Helpers ────────────────────────────────────────

/** Reads one property from parsed JSON without assuming its shape. */
export function property(value: unknown, key: string): unknown {
  return get(value, key);
}

function isSessionTerminationReason(
  value: unknown
): value is SessionTerminationReason {
  return value === 'logout' || value === 'timeout' || value === 'expired';
}

/** Accepts only the message types session coordination understands. */
export function isSessionMessage(value: unknown): value is SessionMessage {
  const type = property(value, 'type');
  const reason = property(value, 'reason');

  if (type === 'SESSION_CLOSED') {
    return (
      typeof property(value, 'sessionId') === 'string' &&
      isSessionTerminationReason(reason)
    );
  }

  if (type === 'CLOSE_OTHER_SESSIONS') {
    return (
      typeof property(value, 'originSessionId') === 'string' &&
      (reason === 'logout' || reason === 'timeout')
    );
  }

  return false;
}

/** Confirms that parsed storage contains a session termination record. */
export function isSessionTerminationRecord(
  value: unknown
): value is SessionTerminationRecord {
  const state = property(value, 'state');

  return (
    typeof property(value, 'sessionId') === 'string' &&
    isSessionTerminationReason(property(value, 'reason')) &&
    (state === 'closing' || state === 'closed' || state === 'uncertain')
  );
}

/** Returns the shared storage key and lock name for one session close. */
export function sessionRecordKey(sessionId: string): string {
  return `${SESSION_RECORD_PREFIX}${encodeURIComponent(sessionId)}`;
}

/**
 * TODO: Remove after Angular apps are retired.
 *
 * Storage key for the latest React activity on one backend session.
 *
 * `encodeURIComponent` keeps `/` and other separators out of the key so Angular
 * and React always read the same slot. Angular's helper is
 * `getSessionActivityKey` and must stay in lockstep until that idle path is
 * deleted with the Angular apps.
 */
export function sessionActivityKey(sessionId: string): string {
  return `${SESSION_ACTIVITY_PREFIX}${encodeURIComponent(sessionId)}`;
}

/** Storage the activity sweep can list. Tests inject a map. */
export interface SessionActivityStorage {
  keys(): string[];
  read(key: string): unknown;
  remove(key: string): void;
}

function browserActivityStorage(): SessionActivityStorage {
  return {
    keys(): string[] {
      if (typeof window === 'undefined' || !window.localStorage) return [];

      const keys: string[] = [];

      for (let index = 0; index < window.localStorage.length; index += 1) {
        const key = window.localStorage.key(index);

        if (key) keys.push(key);
      }

      return keys;
    },
    read: LocalStorage.read,
    remove: LocalStorage.remove
  };
}

/**
 * TODO: Remove after Angular apps are retired.
 *
 * Drops activity stamps Angular would already ignore. A stamp is one key per
 * session id and nothing else removes the ones left by earlier sign-ins.
 * Stamps still inside the idle window stay, including another live tab's.
 */
export function pruneStaleSessionActivity(
  now = Date.now(),
  storage: SessionActivityStorage = browserActivityStorage()
): void {
  storage.keys().forEach((key) => {
    if (!key.startsWith(SESSION_ACTIVITY_PREFIX)) return;

    const updatedAt = property(storage.read(key), 'updatedAt');

    if (
      typeof updatedAt !== 'number' ||
      now - updatedAt >= SESSION_ACTIVITY_TTL_MS
    ) {
      storage.remove(key);
    }
  });
}

/** Returns whether a captured API fault confirms that the session expired. */
export function isConfirmedSessionExpiry(
  error: CapturedError | null | undefined
): boolean {
  if (!error || !ErrorConverter.isCapturedErrorApiFaultError(error)) {
    return false;
  }

  const { faultCode } = error.errorContext;

  return (
    faultCode !== null &&
    faultCode !== undefined &&
    String(faultCode) === SESSION_EXPIRED_FAULT_CODE
  );
}
